import { CountryCode } from '@/i18n/countries';
import { EventCalendarXs, EventCardS, EventsGetFilter } from '@/types/calendar';
import { EventDetails } from '@/types/events';
import { apiBaseUrl } from '@/utils/constants';

import { ENTITIES, locationIdOnline } from './api.constant';

export const events$filterQuery = (eventFilters: EventsGetFilter): string => {
    const filters: any = { _or: [], _and: [] };

    filters._and.push({ _or: [{ hidden: { _eq: false } }, { hidden: { _null: true } }] });

    if (eventFilters.countryCode && !eventFilters.locationCity?.length) {
        filters.company = { company: { countries: { country: { code: { _eq: eventFilters.countryCode } } } } };
    } else if (eventFilters.locationCity?.length) {
        filters.location = { _or: [{ id: locationIdOnline }, { city: { _in: eventFilters.locationCity } }] };
    }

    if (eventFilters.typeLanguage?.length) {
        if (!filters.event_type) filters.event_type = {};
        filters.event_type.languages = { language: { code: { _in: eventFilters.typeLanguage } } };
    }

    if (eventFilters.typeTag?.length) {
        if (!filters.event_type) filters.event_type = {};
        filters.event_type.tags = { tag: { id: { _in: eventFilters.typeTag } } };
    }

    if (eventFilters.dateStart && eventFilters.dateEnd) {
        filters.start_date = { _between: [eventFilters.dateStart, eventFilters.dateEnd] };
    } else if (eventFilters.dateStart) {
        filters.start_date = { _gte: eventFilters.dateStart };
    }

    if (eventFilters.typePrice?.length) {
        if (eventFilters.typePrice.includes('free')) {
            filters._or.push({
                _and: [
                    { price_type: { _eq: 'regular' } },
                    { visit_price: { _eq: 0 } },
                    { _or: [{ hidden: { _eq: false } }, { hidden: { _null: true } }] },
                ],
            });
        }
        if (eventFilters.typePrice.includes('paid')) {
            filters._or.push(
                {
                    _and: [
                        { price_type: { _eq: 'regular' } },
                        { visit_price: { _neq: 0 } },
                        { _or: [{ hidden: { _eq: false } }, { hidden: { _null: true } }] },
                    ],
                },
                {
                    _and: [
                        { price_type: { _in: ['donation', 'unspecified'] } },
                        { _or: [{ hidden: { _eq: false } }, { hidden: { _null: true } }] },
                    ],
                },
            );
        }
    }

    if (eventFilters.eventIds?.length) {
        filters.id = { _in: eventFilters.eventIds };
    }

    const deep = {
        translations: { _filter: { language: { _eq: eventFilters.lang } } },
    };

    const filterStr = Object.keys(filters) ? '&filter=' + encodeURIComponent(JSON.stringify(filters)) : '';
    const deepStr = `&deep=${encodeURIComponent(JSON.stringify(deep))}`;

    return `${filterStr}${deepStr}`;
};

const fields$xs = (['id', 'name', 'start_date', 'status'] satisfies Array<keyof EventCalendarXs>).join(',');

export function events$get$xs(filterQuery: string, limit: number, offset: number): Promise<EventCalendarXs[]> {
    return events$get$part<EventCalendarXs>(fields$xs, filterQuery, limit, offset);
}

export const searchEvents = async ({
    search_text,
    start_date,
    end_date,
}: {
    search_text: string;
    start_date?: string;
    end_date?: string;
}) => {
    const searchStr = [
        search_text ? `search_text=${search_text}` : '',
        start_date ? `&start_date=${start_date}` : '',
        end_date ? `&end_date=${end_date}` : '',
    ].join('');

    const url = `https://recommendation-system.polakohedonist.club/search?${searchStr}`;
    return (await fetch(url).then((res) => res.json())) ?? [];
};

const fields$s = (
    [
        'id',
        'name',
        'start_date',
        'status',
        'base_currency',
        'duration',
        'image_cover.id',
        'image_cover.filename_disk',
        'for_adults',
        'location.name',
        'location.address_link',
        'event_type.image_cover',
        'event_type.name',
        'visit_price',
        'price_type',
        'youtube_video_link',
        'translations.event_name',
        'animated',
    ] satisfies Array<
        | keyof EventCardS
        | `location.${keyof EventCardS['location']}`
        | `event_type.${keyof EventCardS['event_type']}`
        | `translations.${keyof EventCardS['translations'][number]}`
        | `image_cover.${keyof EventCardS['image_cover']}`
    >
).join(',');

export function events$get$s(filterQuery: string, limit: number, offset: number): Promise<EventCardS[]> {
    return events$get$part<EventCardS>(fields$s, filterQuery, limit, offset);
}

const events$get$part = async <T>(
    fieldList: string,
    filterQuery: string,
    limit: number,
    offset: number,
): Promise<T[]> => {
    const url = `${apiBaseUrl}${ENTITIES.events}?fields=${fieldList}${filterQuery}&limit=${limit}&offset=${offset}&&sort[]=start_date`;
    const response = await fetch(url, { next: { revalidate: 600 } }).then((res) => res.json());
    return response?.data ?? [];
};

export const events$get$count = async (filterQuery: string): Promise<number> => {
    const url = `${apiBaseUrl}${ENTITIES.events}?aggregate[count]=id${filterQuery}`;
    const response = await fetch(url, { next: { revalidate: 600 } }).then((res) => res.json());
    return response?.data?.[0]?.count?.id ?? 0;
};

const fields$details = (
    [
        'animated',
        'base_currency',
        'book_link',
        'company.company.id',
        'company.company.name',
        'description',
        'documents.document.document',
        //todo fix types
        // @ts-ignore
        'documents.document.document.filename_disk',
        'documents.document.id',
        'documents.document.language_code',
        'documents.document.translations.language',
        'documents.document.translations.nom',
        'duration',
        'facebook',
        'for_adults',
        'id',
        'image_cover.id',
        'image_cover.filename_disk',
        'instagram',
        'links.link',
        'location.address_link',
        'location.address_text',
        'location.city.country.*',
        'location.city.name',
        'location.id',
        'location.name',
        'name',
        // @ts-ignore
        'payment_currency.currency_code',
        'phone',
        'price_type',
        'seat_schema',
        'start_date',
        'status',
        'telegram',
        'tickets.id',
        'tickets.status',
        'translations.event_description',
        'translations.event_name',
        'event_type.image_cover',
        'event_type.languages.language.code',
        'event_type.languages.language.title',
        'event_type.languages.language.translations.language',
        'event_type.languages.language.translations.nom',
        'event_type.name',
        'event_type.tags.tag.id',
        'event_type.tags.tag.name',
        'visit_price',
        'with_promocode',
        'youtube_video_link',
    ] satisfies Array<
        | keyof EventDetails
        | `company.company.${keyof EventDetails['company'][number]['company']}`
        | `image_cover.${keyof EventDetails['image_cover']}`
        | `location.${keyof EventDetails['location']}`
        | `location.city.${keyof EventDetails['location']['city']}`
        | (`location.city.country.${keyof EventDetails['location']['city']['country']}` | `location.city.country.*`)
        | `event_type.${keyof EventDetails['event_type']}`
        | `event_type.languages.language.${keyof EventDetails['event_type']['languages'][number]['language']}`
        | `event_type.languages.language.translations.${keyof EventDetails['event_type']['languages'][number]['language']['translations'][number]}`
        | `event_type.tags.tag.${keyof EventDetails['event_type']['tags'][number]['tag']}`
        | `documents.document.${keyof EventDetails['documents'][number]['document']}`
        | `documents.document.translations.${keyof EventDetails['documents'][number]['document']['translations'][number]}`
        | `tickets.${keyof EventDetails['tickets'][number]}`
        | `translations.${keyof EventDetails['translations'][number]}`
        | `links.${keyof EventDetails['links'][number]}`
    >
).join(',');

export const events$get$details = async (slug: string, country_code: CountryCode, lang: string) => {
    const filter = { company: { company: { countries: { country: { code: { _eq: country_code } } } } } };
    const deep = {
        translations: { _filter: { language: { _eq: lang } } },
        documents: { _filter: { document: { translations: { language: { _eq: lang } } } } },
        tickets: { _limit: 400 },
    };
    const filterStr = encodeURIComponent(JSON.stringify(filter));
    const deepStr = encodeURIComponent(JSON.stringify(deep));
    const url = `${apiBaseUrl}${ENTITIES.events}/${slug}?fields=${fields$details}&filter=${filterStr}&deep=${deepStr}`;
    const response = await fetch(url, { cache: 'no-cache' }).then((res) => (res.ok ? res.json() : null));
    return response?.data || null;
};

const fields$eventLinks = (
    [
        'name',
        'start_date',
        'translations.event_name',
        'translations.event_description',
        'links.link',
        'id',
    ] satisfies Array<
        | keyof EventDetails
        | `translations.${keyof EventCardS['translations'][number]}`
        | `links.${keyof EventDetails['links'][number]}`
    >
).join(',');

export const getEventLinks = async (links: string[], lang: string) => {
    const filter = { id: { _in: links } };
    const deep = { translations: { _filter: { language: { _eq: lang } } } };
    const filterStr = encodeURIComponent(JSON.stringify(filter));
    const deepStr = encodeURIComponent(JSON.stringify(deep));
    const url = `${apiBaseUrl}${ENTITIES.events}?fields=${fields$eventLinks}&filter=${filterStr}&deep=${deepStr}`;
    const response = await fetch(url, { cache: 'no-cache' }).then((res) => (res.ok ? res.json() : null));
    return response?.data || null;
};

export const getHiddenEventIds = async () => {
    const filter = { _or: [{ hidden: { _eq: true } }, { hidden: { _null: true } }], status: 'scheduled' };
    const url = `${apiBaseUrl}${ENTITIES.events}?fields=id&filter=${encodeURIComponent(JSON.stringify(filter))}`;
    const response = await fetch(url).then((res) => res.json());
    return response.data ?? [];
};
